import { neutral, primary } from "app/themes/colors";

import type {
  ScreenshotBackground,
  ScreenshotPadding
} from "./screenshot.types";

export interface ComposeScreenshotOptions {
  /** The raw page capture from `capturePage`. */
  capture: HTMLCanvasElement;
  background: ScreenshotBackground;
  padding: ScreenshotPadding;
  /** From `loadSunoLogo`; `null` falls back to a drawn wordmark. */
  logo: HTMLImageElement | null;
}

/** Tints for the wordmark, picked by `background.isDark`. */
export const LOGO_LIGHT_COLOR = "#FFFFFF";
export const LOGO_DARK_COLOR = primary[500];

const MIN_PADDING = 24;
const MAX_PADDING = 320;
const MIN_RADIUS = 6;
const MAX_RADIUS = 28;
const MIN_LOGO_HEIGHT = 14;
const MAX_LOGO_HEIGHT = 40;
const MIN_BLUR = 12;
const MAX_BLUR = 90;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * `CanvasRenderingContext2D.roundRect` is recent enough that the repo's browser
 * floor does not guarantee it, and jsdom has it in neither case — so the path is
 * traced by hand. Every rounded edge in the frame goes through this.
 */
export const traceRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  const r = clamp(radius, 0, Math.min(width, height) / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

export interface ScreenshotLayout {
  outputWidth: number;
  outputHeight: number;
  /** Margin on the left, right and top of the framed capture. */
  padding: number;
  /** Bottom band: at least `padding`, widened when the wordmark needs the room. */
  footer: number;
  radius: number;
  logoHeight: number;
}

/**
 * Where everything lands, in the capture's own device pixels.
 *
 * Exported because it is the part worth asserting: a test can check that a
 * capture comes back framed at the right size without a real 2D context.
 */
export const measureScreenshot = (
  captureWidth: number,
  captureHeight: number,
  padding: ScreenshotPadding
): ScreenshotLayout => {
  const pad = clamp(
    Math.round(captureWidth * padding.ratio),
    MIN_PADDING,
    MAX_PADDING
  );
  const logoHeight = clamp(
    Math.round(captureWidth * 0.022),
    MIN_LOGO_HEIGHT,
    MAX_LOGO_HEIGHT
  );
  // The wordmark sits in the bottom margin rather than on top of the capture,
  // so that margin has to be tall enough to hold it with air on both sides even
  // when the user picked "Snug".
  const footer = Math.max(pad, Math.round(logoHeight * 2.5));
  return {
    outputWidth: captureWidth + pad * 2,
    outputHeight: captureHeight + pad + footer,
    padding: pad,
    footer,
    radius: clamp(Math.round(captureWidth * 0.009), MIN_RADIUS, MAX_RADIUS),
    logoHeight
  };
};

const paintBackground = (
  ctx: CanvasRenderingContext2D,
  background: ScreenshotBackground,
  capture: HTMLCanvasElement,
  layout: ScreenshotLayout
) => {
  const { outputWidth, outputHeight } = layout;

  if (background.kind === "none") return;

  if (background.kind === "blur") {
    ctx.save();
    ctx.filter = `blur(${clamp(
      Math.round(Math.max(outputWidth, outputHeight) * 0.035),
      MIN_BLUR,
      MAX_BLUR
    )}px)`;
    // Oversized on purpose: a blur samples past the edge of what it is given,
    // so a backdrop drawn at exactly the canvas size fades to transparent at
    // all four borders and the frame ends up sitting in a pale vignette.
    const scale =
      Math.max(outputWidth / capture.width, outputHeight / capture.height) *
      1.3;
    const drawWidth = capture.width * scale;
    const drawHeight = capture.height * scale;
    ctx.drawImage(
      capture,
      (outputWidth - drawWidth) / 2,
      (outputHeight - drawHeight) / 2,
      drawWidth,
      drawHeight
    );
    ctx.restore();
    // Mutes the echo so the framed capture still reads as the subject.
    ctx.fillStyle = "rgba(255, 255, 255, 0.38)";
    ctx.fillRect(0, 0, outputWidth, outputHeight);
    return;
  }

  if (background.kind === "gradient") {
    const gradient = ctx.createLinearGradient(0, 0, outputWidth, outputHeight);
    gradient.addColorStop(0, background.colors[0]);
    gradient.addColorStop(1, background.colors[1] ?? background.colors[0]);
    ctx.fillStyle = gradient;
  } else {
    ctx.fillStyle = background.colors[0] ?? neutral[100];
  }
  ctx.fillRect(0, 0, outputWidth, outputHeight);
};

const paintFrame = (
  ctx: CanvasRenderingContext2D,
  capture: HTMLCanvasElement,
  background: ScreenshotBackground,
  layout: ScreenshotLayout
) => {
  const { padding, radius } = layout;

  // Shadow pass. Cast off an opaque rounded rect rather than off the capture
  // itself — a shadow on `drawImage` is computed from the image's alpha, and a
  // page capture is fully opaque and square, so the corners would cast a
  // square shadow past the rounded clip.
  ctx.save();
  ctx.shadowColor = background.isDark
    ? "rgba(0, 0, 0, 0.45)"
    : "rgba(15, 16, 16, 0.22)";
  ctx.shadowBlur = Math.round(padding * 0.7);
  ctx.shadowOffsetY = Math.round(padding * 0.25);
  ctx.fillStyle = "#FFFFFF";
  traceRoundedRect(
    ctx,
    padding,
    padding,
    capture.width,
    capture.height,
    radius
  );
  ctx.fill();
  ctx.restore();

  ctx.save();
  traceRoundedRect(
    ctx,
    padding,
    padding,
    capture.width,
    capture.height,
    radius
  );
  ctx.clip();
  ctx.drawImage(capture, padding, padding, capture.width, capture.height);
  ctx.restore();

  // Hairline: separates a white page from a light backdrop, which the shadow
  // alone is too soft to do.
  ctx.save();
  ctx.strokeStyle = background.isDark
    ? "rgba(255, 255, 255, 0.28)"
    : "rgba(9, 10, 10, 0.10)";
  ctx.lineWidth = Math.max(1, Math.round(capture.width / 1200));
  traceRoundedRect(
    ctx,
    padding,
    padding,
    capture.width,
    capture.height,
    radius
  );
  ctx.stroke();
  ctx.restore();
};

const paintWordmark = (
  ctx: CanvasRenderingContext2D,
  logo: HTMLImageElement | null,
  background: ScreenshotBackground,
  capture: HTMLCanvasElement,
  layout: ScreenshotLayout
) => {
  const { outputWidth, padding, footer, logoHeight } = layout;
  const bandTop = padding + capture.height;
  const centreY = bandTop + footer / 2;
  const color = background.isDark ? LOGO_LIGHT_COLOR : LOGO_DARK_COLOR;

  ctx.save();
  ctx.globalAlpha = background.kind === "none" ? 1 : 0.9;

  if (logo && logo.naturalHeight > 0) {
    const scale = logoHeight / logo.naturalHeight;
    const logoWidth = logo.naturalWidth * scale;
    ctx.drawImage(
      logo,
      outputWidth - padding - logoWidth,
      centreY - logoHeight / 2,
      logoWidth,
      logoHeight
    );
  } else {
    // The asset failed to load. A plain wordmark still brands the export, and
    // is better than shipping the user an unbranded image with no explanation.
    ctx.fillStyle = color;
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.font = `600 ${Math.round(logoHeight * 0.95)}px Poppins, Helvetica, Arial, sans-serif`;
    ctx.fillText("suno", outputWidth - padding, centreY);
  }

  ctx.restore();
};

/**
 * Frames a page capture: backdrop, rounded drop-shadowed card, Suno wordmark.
 *
 * Synchronous and self-contained — the logo is loaded by the caller and handed
 * in — so re-running it on every option change is a paint, not a pipeline.
 */
export default function composeScreenshot({
  capture,
  background,
  padding,
  logo
}: ComposeScreenshotOptions): HTMLCanvasElement {
  if (capture.width <= 0 || capture.height <= 0) {
    throw new Error("Cannot frame an empty capture");
  }

  const layout = measureScreenshot(capture.width, capture.height, padding);
  const canvas = document.createElement("canvas");
  canvas.width = layout.outputWidth;
  canvas.height = layout.outputHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");

  ctx.clearRect(0, 0, layout.outputWidth, layout.outputHeight);
  paintBackground(ctx, background, capture, layout);
  paintFrame(ctx, capture, background, layout);
  paintWordmark(ctx, logo, background, capture, layout);

  return canvas;
}
